import { useInfiniteQuery } from "@tanstack/react-query";
import { leadsApi } from "../../../libs/landvoice-api";
import { GetLeadsOptions } from "../../../types/neighborhoodSearch";
import { getListingFromLead } from "../../../utils/neighborhoodSearch";
import {
  BEDS_MIN,
  BEDS_MAX,
  BATHS_MIN,
  BATHS_MAX,
  ACREAGE_MIN,
  ACREAGE_MAX,
  SQFT_MIN,
  SQFT_MAX,
  YEAR_BUILT_MIN,
} from "src/constants/neighborhoodSearch";
import { getMinMaxQueryParams } from "../utils/getMinMaxQueryParams";
import { generateSearchListingsQueryKey } from "../utils/generateSearchListingsQueryKey";
import { getYearBuiltMax } from "../../../utils/neighborhoodSearch";

export const useInfiniteSearchListingsQuery = ({
  bedrooms,
  bathrooms,
  acreage,
  squareFootage,
  yearBuilt,
  propertyType,
  favorite,
  tag = "",
  note = "",
  companyOwned = undefined,
  showHidden = undefined,
  onlyHidden = undefined,
  listId = [],
  pageSize = 50,
}: GetLeadsOptions) => {
  return useInfiniteQuery({
    queryKey: generateSearchListingsQueryKey({
      bedrooms,
      bathrooms,
      acreage,
      squareFootage,
      yearBuilt,
      propertyType,
      favorite,
      tag,
      note,
      companyOwned,
      showHidden,
      onlyHidden,
      listId,
      pageSize,
      queryType: "infinite",
    }),
    initialPageParam: 0,
    queryFn: async ({ pageParam = 0 }) => {
      const offset = pageParam;
      const { data } = await leadsApi.getLeadsWithCount(
        getMinMaxQueryParams(
          Number(bedrooms?.[0]),
          Number(bedrooms?.[1]),
          BEDS_MIN,
          BEDS_MAX
        ),
        getMinMaxQueryParams(
          Number(bathrooms?.[0]),
          Number(bathrooms?.[1]),
          BATHS_MIN,
          BATHS_MAX
        ),
        getMinMaxQueryParams(
          Number(acreage?.[0]),
          Number(acreage?.[1]),
          ACREAGE_MIN,
          ACREAGE_MAX
        ),
        getMinMaxQueryParams(
          Number(squareFootage?.[0]),
          Number(squareFootage?.[1]),
          SQFT_MIN,
          SQFT_MAX
        ),
        getMinMaxQueryParams(
          Number(yearBuilt?.[0]),
          Number(yearBuilt?.[1]),
          YEAR_BUILT_MIN,
          getYearBuiltMax()
        ),
        propertyType,
        favorite,
        tag,
        note,
        companyOwned,
        showHidden,
        onlyHidden,
        listId,
        offset,
        pageSize
      );

      return {
        listings: (data.leads || []).map((lead) => getListingFromLead(lead)),
        total_records: data.total_count,
      };
    },
    getNextPageParam: (lastPage, allPages) => {

      // Calculate the total number of records fetched so far
      const totalFetched = allPages.reduce((acc, page) => acc + page.listings.length, 0);
      
      // If we've fetched all available records, return undefined to stop pagination
      if (totalFetched >= lastPage.total_records) {
        return undefined;
      }
      
      // Return the next offset
      return totalFetched;
    },
    getPreviousPageParam: (firstPage, allPages) => {
      return firstPage.listings.length - pageSize;
    },
    enabled: !!listId?.length,
  });
};